import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CheckCircle, Clock, CalendarDays, Edit } from 'lucide-react';
import { format, parseISO } from 'date-fns';

const formatCategory = (gl) =>
  gl ? gl.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()) : '';

export function PositionDetailsDialog({ position, open, onOpenChange, onEdit }) {
  if (!position) return null;

  const dates = position.dates && position.dates.length
    ? position.dates
    : [position.start_date || position.date].filter(Boolean);
  const sorted = [...dates].sort();
  const datesDisplay = sorted
    .map((d) => {
      try { return format(parseISO(d), 'EEE, MMM d, yyyy'); } catch (_e) { return d; }
    })
    .join('  •  ');

  const statusLabel = position.status
    ? position.status.charAt(0).toUpperCase() + position.status.slice(1)
    : 'Open';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="position-details-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <Badge className={`status-${position.status}`}>{statusLabel}</Badge>
            <span>{formatCategory(position.grade_level) || 'Open Position'}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <div className="flex items-start gap-2 text-slate-700">
            <CalendarDays className="h-4 w-4 mt-0.5 text-slate-400 flex-shrink-0" />
            <span data-testid="details-dates">{datesDisplay}</span>
          </div>
          <div className="flex items-center gap-2 text-slate-700">
            <Clock className="h-4 w-4 text-slate-400" />
            <span>{position.start_time} - {position.end_time}</span>
          </div>
          <div className="text-slate-700">
            <span className="text-slate-400">Position Category:</span>{' '}
            {formatCategory(position.grade_level) || '—'}
          </div>
          {position.description && (
            <div className="text-slate-600">
              <span className="text-slate-400">Special Instructions:</span> {position.description}
            </div>
          )}
          {position.accepted_by_name ? (
            <p className="text-emerald-600 flex items-center gap-1">
              <CheckCircle className="h-4 w-4" />
              Filled by {position.accepted_by_name}
            </p>
          ) : (
            position.status === 'open' && (
              <p className="text-slate-500">
                Offered to {position.target_tocs?.length || 0} sub{(position.target_tocs?.length || 0) !== 1 ? 's' : ''}
              </p>
            )
          )}
        </div>

        {onEdit && (
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { onOpenChange(false); onEdit(position); }}
              data-testid="details-edit-btn"
            >
              <Edit className="h-4 w-4 mr-1" />
              Edit Position
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
